(() => {
  const initSizeFitExperience = (root) => {
    if (!root || root.dataset.sizeFitReady === 'true') return;
    root.dataset.sizeFitReady = 'true';

    const cleanups = [];
    const sunglassesToggle = document.querySelector('[data-sunglasses-toggle]');

    if (sunglassesToggle) {
      const syncSunglassesMode = () => {
        root.dataset.sunglassesActive = sunglassesToggle.getAttribute('aria-pressed') === 'true' ? 'true' : 'false';
      };
      const sunglassesObserver = new MutationObserver(syncSunglassesMode);

      syncSunglassesMode();
      sunglassesObserver.observe(sunglassesToggle, {
        attributes: true,
        attributeFilter: ['aria-pressed'],
      });
      cleanups.push(() => sunglassesObserver.disconnect());
    }

    const heroVideo = root.querySelector('[data-hero-video]');
    const heroMotionToggle = root.querySelector('[data-hero-motion-toggle]');

    if (heroVideo && heroMotionToggle) {
      const heroMotionLabel = heroMotionToggle.querySelector('[data-hero-motion-label]');
      const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');

      const syncHeroMotionControl = () => {
        const paused = heroVideo.paused;
        heroMotionToggle.classList.toggle('is-paused', paused);
        heroMotionToggle.setAttribute('aria-label', paused ? 'Play blueprint drawing' : 'Pause blueprint drawing');
        if (heroMotionLabel) heroMotionLabel.textContent = paused ? 'Play drawing' : 'Pause drawing';
      };

      const playHeroVideo = () => {
        const playback = heroVideo.play();
        if (playback) playback.catch(syncHeroMotionControl);
      };

      const applyMotionPreference = () => {
        if (motionPreference.matches) {
          heroVideo.pause();
          heroVideo.currentTime = 0;
        } else {
          playHeroVideo();
        }
        syncHeroMotionControl();
      };

      const toggleHeroMotion = () => {
        if (heroVideo.paused) playHeroVideo();
        else heroVideo.pause();
      };

      heroMotionToggle.addEventListener('click', toggleHeroMotion);
      heroVideo.addEventListener('play', syncHeroMotionControl);
      heroVideo.addEventListener('pause', syncHeroMotionControl);
      motionPreference.addEventListener('change', applyMotionPreference);
      applyMotionPreference();

      cleanups.push(() => {
        heroMotionToggle.removeEventListener('click', toggleHeroMotion);
        heroVideo.removeEventListener('play', syncHeroMotionControl);
        heroVideo.removeEventListener('pause', syncHeroMotionControl);
        motionPreference.removeEventListener('change', applyMotionPreference);
      });
    }

    const storySection = root.querySelector('[data-story-section]');

    if (storySection) {
      const images = Array.from(storySection.querySelectorAll('[data-story-image]'));
      const copies = Array.from(storySection.querySelectorAll('[data-story-copy]'));
      const progress = Array.from(storySection.querySelectorAll('[data-story-progress]'));
      const number = storySection.querySelector('[data-story-number]');
      let activeStory = -1;
      let frame = 0;

      const setStory = (nextStory) => {
        if (nextStory === activeStory || nextStory < 0 || nextStory >= images.length) return;
        activeStory = nextStory;

        images.forEach((image, index) => {
          const active = index === activeStory;
          image.classList.toggle('is-active', active);
          image.setAttribute('aria-hidden', active ? 'false' : 'true');
        });

        copies.forEach((copy, index) => {
          const active = index === activeStory;
          copy.classList.toggle('is-active', active);
          copy.setAttribute('aria-hidden', active ? 'false' : 'true');
        });

        progress.forEach((item, index) => item.classList.toggle('is-active', index === activeStory));
        if (number) number.textContent = String(activeStory + 1).padStart(2, '0');
      };

      const updateStory = () => {
        frame = 0;
        if (!images.length) return;

        const rect = storySection.getBoundingClientRect();
        const travel = Math.max(storySection.offsetHeight - window.innerHeight, 1);
        const ratio = Math.min(0.999, Math.max(0, -rect.top / travel));
        const nextStory = Math.min(images.length - 1, Math.floor(ratio * images.length));
        setStory(nextStory);
      };

      const onScroll = () => {
        if (frame) return;
        frame = window.requestAnimationFrame(updateStory);
      };

      setStory(0);
      updateStory();
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      cleanups.push(() => {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
        if (frame) window.cancelAnimationFrame(frame);
      });
    }

    const dimensionTriggers = Array.from(root.querySelectorAll('[data-dimension-trigger]'));
    const dimensionPanels = Array.from(root.querySelectorAll('[data-dimension-panel]'));

    dimensionTriggers.forEach((trigger) => {
      const onClick = () => {
        const target = trigger.dataset.dimensionTrigger;

        dimensionTriggers.forEach((button) => {
          button.setAttribute('aria-pressed', button === trigger ? 'true' : 'false');
        });

        dimensionPanels.forEach((panel) => {
          const active = panel.dataset.dimensionPanel === target;
          panel.hidden = !active;
          panel.classList.toggle('is-active', active);
        });
      };

      trigger.addEventListener('click', onClick);
      cleanups.push(() => trigger.removeEventListener('click', onClick));
    });

    root.sizeFitCleanup = () => {
      cleanups.forEach((cleanup) => cleanup());
      delete root.dataset.sizeFitReady;
    };
  };

  const initWithin = (scope = document) => {
    if (scope.matches && scope.matches('[data-size-fit-root]')) initSizeFitExperience(scope);
    scope.querySelectorAll?.('[data-size-fit-root]').forEach(initSizeFitExperience);
  };

  initWithin(document);

  document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.('[data-size-fit-root]')
      ? event.target
      : event.target.querySelector?.('[data-size-fit-root]');
    root?.sizeFitCleanup?.();
  });
})();
